const COLOR_HEX_FORMAT = /([0-9A-F]{6}$)|([0-9A-F]{3}$)/

const uploadCarePathRegex = /^https:\/\/ucarecdn.com\/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\//i

const uploadCareCropRegex = /.*\/(-\/crop\/\d+x\d*\/[^/]*\/)/i

type ModifierFunction = (url: string, ...args: any[]) => string
type ValidationFunction = (...args: any[]) => boolean

const isUploadcareUrl = (url: string): boolean => Boolean(url && url.match(uploadCarePathRegex))

const executeIfUploadcareUrl = (fn: ModifierFunction, argsValidation: ValidationFunction = () => true) => (url: string, ...args: any[]): string => {
  if (argsValidation(...args) && isUploadcareUrl(url)) return fn(url, ...args)
  return url
}

const modifierValidation = {
  preview: (dimensions: string): boolean => typeof dimensions === 'string' && !!dimensions.match(/^[0-9]+x[0-9]+$/),
  resize: (dimensions: string): boolean => typeof dimensions === 'string' && (!!dimensions.match(/^[0-9]*x[0-9]+$/) || !!dimensions.match(/^[0-9]+x[0-9]*$/)),
  progressive: (param: string): boolean => param === 'yes' || param === 'no',
  format: (param: string): boolean => ['jpeg', 'png', 'webp', 'auto'].some(x => x === param),
  stretch: (mode: string): boolean => ['on', 'off', 'fill'].some(x => x === mode),
  setfill: (color: string): boolean => typeof color === 'string' && COLOR_HEX_FORMAT.test(color)
}

const preview = executeIfUploadcareUrl(
  (url: string, dimensions: string) => url.includes('/-/preview')
    ? url.replace(/\/-\/preview\//, `/-/preview/${dimensions}/`)
    : `${url}-/preview/${dimensions}/`,
  modifierValidation.preview
)

const resize = executeIfUploadcareUrl(
  (url: string, dimensions: string) => `${url}-/resize/${dimensions}/`,
  modifierValidation.resize
)

const progressive = executeIfUploadcareUrl(
  (url: string, param: string) => `${url}-/progressive/${param}/`,
  modifierValidation.progressive
)

const format = executeIfUploadcareUrl(
  (url: string, param: string) => `${url}-/format/${param}/`,
  modifierValidation.format
)

const stretch = executeIfUploadcareUrl(
  (url: string, mode: string) => `${url}-/stretch/${mode}/`,
  modifierValidation.stretch
)

const setfill = executeIfUploadcareUrl(
  (url: string, color: string) => `${url}-/setfill/${color}/`,
  modifierValidation.setfill
)

type ModifierFunctions = {
  preview: ModifierFunction;
  resize: ModifierFunction;
  progressive: ModifierFunction;
  format: ModifierFunction;
  stretch: ModifierFunction;
  setfill: ModifierFunction;
  [key: string]: ModifierFunction;
}

const modifierFunctions: ModifierFunctions = {
  preview,
  resize,
  progressive,
  format,
  stretch,
  setfill
}

type Modifiers = {
  format?: string;
  preview?: string;
  resize?: string;
  progressive?: string;
  stretch?: string;
  setfill?: string;
  [key: string]: string | undefined;
}

const defaultModifiers: Modifiers = { format: 'auto' }

export const getNamelessUploadcareUrl = (url: string): string => {
  const matched = url && url.match(uploadCarePathRegex)
  return (matched && matched[0]) || url
}

const getUploadcareCropPath = (url: string): string | undefined => {
  const cropPath = url && url.match(uploadCareCropRegex)
  return cropPath?.[1] || undefined
}

const limit = (value: number): number => Math.ceil(Math.min(value, 3000))

const ratios = [0.25, 0.5, 1, 2, 3, 4]

interface GenerateImageSourcesParams {
  url?: string;
  baseW: number;
}

export const generateImageSources = ({
  url = '',
  baseW
}: GenerateImageSourcesParams) => {
  let cleanUrl = getNamelessUploadcareUrl(url)
  const cropPath = getUploadcareCropPath(url)
  cleanUrl = cropPath ? `${cleanUrl}${cropPath}` : cleanUrl
  const src = applyUploadcareModifiers(cleanUrl, { resize: `${limit(baseW)}x` })
  const srcSet = ratios.map(ratio => `${applyUploadcareModifiers(cleanUrl, { resize: `${limit(ratio * baseW)}x` })} ${limit(ratio * baseW)}w`).join(', ')
  return { src, srcSet }
}

export const applyUploadcareModifiers = (url: string, _modifiers: Modifiers = {}): string => {
  const modifiers = { ...defaultModifiers, ..._modifiers }
  return Object.keys(modifiers).reduce(
    (acc, key) => {
      const modifier = modifiers[key]
      const modifierFn = modifierFunctions[key]
      return modifierFn && modifier ? modifierFn(acc, modifier) : acc
    },
    url
  )
}
